import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, updateDoc, doc } from 'firebase/firestore';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { Copy, Check, ArrowLeft, Users, Activity, TrendingDown, BadgeCheck, Clock, Flag, Trophy } from 'lucide-react';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { toDateSafe, getConvertedToClientDate } from '../utils/accountLifecycle';
import {
  fetchConferences,
  fetchAttributionData,
  buildConferenceReport,
  getPeriodStart,
  formatMoney,
} from '../utils/conferenceAttribution';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GlassStyles,
  GlassBackdrop,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassFigure,
  GlassStat,
  GlassChip,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassTooltip,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const colors = GLASS_PALETTE.slice(0, 8);
const sourceColors = GLASS_PALETTE;

const TAB_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Lost', label: 'Lost' },
  { value: 'Won', label: 'Won' },
];

const TIME_OPTIONS = [
  { value: 'All Time', label: 'All time' },
  { value: 'This Year', label: 'Year' },
  { value: 'This Quarter', label: 'Quarter' },
  { value: 'This Month', label: 'Month' },
];

const PERIOD_PHRASE = { 'All Time': 'on record', 'This Year': 'this year', 'This Quarter': 'this quarter', 'This Month': 'this month' };

// Shared field readers so the table and the copy-to-clipboard export always agree
const getCompanyName = (l) => l.company || l.accountName || l.businessName || l.organization || l.companyName || l.business || '';
const getContactName = (l) => {
  const fullName = `${l.contact?.firstName || ''} ${l.contact?.lastName || ''}`.trim();
  if (fullName) return fullName;
  return l.contactName || l.primaryContactName || l.name || '';
};
const getEmail = (l) => l.email || l.contact?.email || l.contactEmail || l.primaryEmail || '';
const getPhone = (l) => l.phone || l.contact?.phone || l.contactPhone || l.primaryPhone || '';

const resultTone = (result) => {
  const r = (result || '').toString().toLowerCase();
  if (r === 'won') return 'mint';
  if (r === 'lost') return 'salmon';
  return 'neutral';
};

const GlassEmpty = ({ className = '', children }) => (
  <div className={`flex items-center justify-center text-center text-sm text-gray-500 dark:text-white/40 ${className}`}>{children}</div>
);

// Donut ring with a faint full-circle track behind the coloured segments
const GlassDonut = ({ data, dataKey, nameKey, inner, outer, darkMode, unit, children }) => {
  const chart = glassChartTheme(darkMode);
  return (
    <div className="relative h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={[{ v: 1 }]} dataKey="v" innerRadius={inner} outerRadius={outer} fill={chart.ringBg} stroke="none" isAnimationActive={false} />
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            innerRadius={inner}
            outerRadius={outer}
            paddingAngle={data.length > 1 ? 3 : 0}
            cornerRadius={8}
            stroke="none"
            animationDuration={800}
          >
            {data.map((entry, idx) => (
              <Cell key={`d-${idx}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<GlassTooltip unit={unit} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

// Horizontal bar breakdown (reasons / stages) on a rounded track
const BreakdownCard = ({ title, data, labelKey, emptyText, darkMode, delay }) => {
  const chart = glassChartTheme(darkMode);
  return (
    <GlassCard className="p-6" delay={delay}>
      <GlassCardHeader title={title} />
      <div className="mt-5 h-80">
        {data.length === 0 ? (
          <GlassEmpty className="h-full">{emptyText}</GlassEmpty>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
              <CartesianGrid horizontal={false} stroke={chart.grid} strokeDasharray="4 6" />
              <XAxis type="number" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis dataKey={labelKey} type="category" width={130} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: chart.cursor }} content={<GlassTooltip unit="leads" />} />
              <Bar dataKey="count" name="Count" radius={[0, 12, 12, 0]} maxBarSize={30} background={{ fill: chart.track, radius: 12 }} animationDuration={700}>
                {data.map((entry, idx) => (
                  <Cell key={`b-${idx}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassCard>
  );
};

const ConferencesCard = ({ rows, totals, unspecified, timeFilter, onSelect, delay }) => {
  const maxLeads = rows.reduce((m, r) => Math.max(m, r.leads), unspecified.leads) || 1;
  const roiTone = totals.roi === null ? 'muted' : totals.roi >= 0 ? 'mint' : 'salmon';

  const tiles = [
    { label: 'Conferences', value: totals.conferences, tone: 'strong' },
    { label: 'Leads', value: totals.leads, tone: 'blue' },
    { label: 'Clients Acquired', value: totals.clients, tone: 'mint' },
    { label: 'MRR', value: formatMoney(totals.mrr), tone: 'mint' },
    { label: 'Total Cost', value: formatMoney(totals.cost), tone: 'salmon' },
    { label: 'Revenue', value: formatMoney(totals.revenue), tone: 'mint' },
    { label: 'ROI', value: totals.roi === null ? '—' : `${totals.roi}%`, tone: roiTone },
  ];

  const displayRows = unspecified.leads > 0
    ? [...rows, {
        key: '__unspecified',
        id: null,
        name: 'Conference (unspecified)',
        location: 'Lead source not tied to a specific conference',
        type: '',
        dateLabel: '-',
        leads: unspecified.leads,
        clients: unspecified.clients,
        revenue: unspecified.revenue,
        mrr: unspecified.mrr,
        cost: 0,
        roi: null,
        costPerClient: null,
        recordedLeads: 0,
        recordedClients: 0,
      }]
    : rows;

  return (
    <GlassCard className="p-6" delay={delay}>
      <GlassCardHeader title="Conferences" to="/crm/conferences-reports" toTitle="Open conference reports" />
      <p className="mb-5 mt-1 text-xs text-gray-500 dark:text-white/[0.45]">
        {timeFilter} · conferences by event date. Leads, clients, MRR and revenue come from matching accounts; only cost is entered by hand.
      </p>

      {displayRows.length === 0 ? (
        <GlassEmpty className="h-32">No conferences found for selected period</GlassEmpty>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
            {tiles.map(tile => (
              <div key={tile.label} className={`${GLASS_INSET} px-4 py-3`}>
                <div className="text-[11px] text-gray-500 dark:text-white/[0.45]">{tile.label}</div>
                <div className={`mt-1 text-xl font-semibold tabular-nums ${GLASS_TEXT[tile.tone]}`}>{tile.value}</div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Conference</th>
                  <th className={GLASS_TH}>Date</th>
                  <th className={GLASS_TH}>Type</th>
                  <th className={`${GLASS_TH} w-56`}>Leads</th>
                  <th className={GLASS_TH}>Clients</th>
                  <th className={GLASS_TH}>MRR</th>
                  <th className={GLASS_TH}>Cost</th>
                  <th className={GLASS_TH}>Revenue</th>
                  <th className={GLASS_TH}>Cost / Client</th>
                  <th className={GLASS_TH}>ROI</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-white/[0.85]">
                {displayRows.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => row.id && onSelect(row.id)}
                    className={`${GLASS_ROW} ${row.id ? 'cursor-pointer' : ''}`}
                  >
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900 dark:text-white">{row.name}</div>
                      {row.location && (
                        <div className="text-xs text-gray-500 dark:text-white/40">{row.location}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{row.dateLabel}</td>
                    <td className="py-3 pr-4">
                      {row.type ? (
                        <GlassBadge tone={row.type === 'Speaker' ? 'lavender' : row.type === 'Sponsor' ? 'blue' : 'neutral'}>{row.type}</GlassBadge>
                      ) : '-'}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="w-6 font-semibold tabular-nums">{row.leads}</span>
                        <div className="h-1.5 flex-1 rounded-full bg-black/[0.06] dark:bg-white/[0.07]">
                          <div className="h-1.5 rounded-full" style={{ width: `${(row.leads / maxLeads) * 100}%`, backgroundColor: GLASS_TONE.blue }} />
                        </div>
                      </div>
                      {row.recordedLeads > 0 && row.recordedLeads !== row.leads && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-white/40">{row.recordedLeads} recorded</div>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`font-semibold ${GLASS_TEXT.mint}`}>{row.clients}</span>
                      {row.recordedClients > 0 && row.recordedClients !== row.clients && (
                        <div className="text-xs text-gray-500 dark:text-white/40">{row.recordedClients} recorded</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 tabular-nums">{formatMoney(row.mrr)}</td>
                    <td className="py-3 pr-4 tabular-nums">{formatMoney(row.cost)}</td>
                    <td className="py-3 pr-4 tabular-nums">{formatMoney(row.revenue)}</td>
                    <td className="py-3 pr-4 tabular-nums">{row.costPerClient === null ? '—' : formatMoney(row.costPerClient)}</td>
                    <td className={`py-3 font-semibold ${GLASS_TEXT[row.roi === null ? 'muted' : row.roi >= 0 ? 'mint' : 'salmon']}`}>
                      {row.roi === null ? '—' : `${row.roi}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </GlassCard>
  );
};

const SourcesCard = ({ data, darkMode, timeFilter, delay }) => {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  return (
    <GlassCard className="flex flex-col p-6" delay={delay}>
      <GlassCardHeader title="Sources" />
      {data.length === 0 ? (
        <GlassEmpty className="h-64">No source data available</GlassEmpty>
      ) : (
        <>
          <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-white/[0.45]">
            How the {total} leads {PERIOD_PHRASE[timeFilter] || ''} reached us.
          </p>
          <div className="my-4 h-52">
            <GlassDonut data={data} dataKey="count" nameKey="source" inner={74} outer={84} darkMode={darkMode} unit="leads">
              <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{total}</div>
              <div className="text-xs text-gray-500 dark:text-white/[0.45]">leads</div>
            </GlassDonut>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-1 content-start gap-x-6 gap-y-3 overflow-y-auto pr-1 sm:grid-cols-2 xl:max-h-44">
            {data.map((entry) => (
              <div key={entry.source} className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-white/75" title={entry.source}>{entry.source}</span>
                <span className="font-semibold text-gray-900 tabular-nums dark:text-white">{entry.count}</span>
                <span className="w-10 text-right text-gray-500 tabular-nums dark:text-white/40">{entry.share}%</span>
              </div>
            ))}
          </div>
        </>
      )}
    </GlassCard>
  );
};

const LostLeadsReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [allLeads, setAllLeads] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [attribution, setAttribution] = useState({ accounts: [], purchases: [] });
  const [timeFilter, setTimeFilter] = useState('This Year');
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editLead, setEditLead] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editStageBeforeLost, setEditStageBeforeLost] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPostMortem, setEditPostMortem] = useState(false);

  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const accountsRef = collection(db, 'accounts');
        // Won leads are converted in place (type flips to Clients), so both types are fetched;
        // filter by tab client-side
        const [leadSnap, clientSnap] = await Promise.all([
          getDocs(query(accountsRef, where('type', '==', 'Leads'), orderBy('createdAt', 'desc'))),
          getDocs(query(accountsRef, where('type', '==', 'Clients'))),
        ]);
        const leadRows = leadSnap.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
            receivedDate: d.receivedDate?.toDate?.() || (d.receivedDate instanceof Date ? d.receivedDate : (d.receivedDate?.seconds ? new Date(d.receivedDate.seconds * 1000) : (typeof d.receivedDate === 'string' ? new Date(d.receivedDate) : null))),
            lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
            wonAt: d.wonAt?.toDate?.() || (d.wonAt instanceof Date ? d.wonAt : null),
            convertedAt: d.convertedAt?.toDate?.() || (d.convertedAt instanceof Date ? d.convertedAt : null),
            lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
          };
        });
        // Clients read their lead-era fields from leadSnapshot (stage is the program tier now)
        const clientRows = clientSnap.docs.map(doc => {
          const d = doc.data();
          const snap = d.leadSnapshot && typeof d.leadSnapshot === 'object' ? d.leadSnapshot : {};
          return {
            id: doc.id,
            ...d,
            isClient: true,
            result: 'Won',
            stage: 'Client',
            wonStage: snap.stage || null,
            leadSource: d.leadSource || snap.leadSource || d.source || null,
            qualified: typeof d.qualified === 'boolean' ? d.qualified : snap.qualified,
            createdAt: toDateSafe(d.createdAt),
            receivedDate: toDateSafe(d.receivedDate) || toDateSafe(snap.receivedDate),
            lostAt: null,
            wonAt: getConvertedToClientDate(d) || toDateSafe(d.program?.startDate) || toDateSafe(d.createdAt),
            convertedAt: toDateSafe(d.convertedToClientAt),
            lastActivityAt: toDateSafe(d.lastActivityAt),
          };
        });
        setAllLeads([...leadRows, ...clientRows]);
      } catch (err) {
        console.error('Failed to fetch leads', err);
        setAllLeads([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  // Conference performance is derived from accounts + purchases, not hand-entered numbers
  useEffect(() => {
    const loadConferenceData = async () => {
      try {
        const [conferenceDocs, attribution] = await Promise.all([
          fetchConferences(),
          fetchAttributionData(),
        ]);
        setConferences(conferenceDocs);
        setAttribution(attribution);
      } catch (err) {
        console.error('Failed to load conference data', err);
      }
    };
    loadConferenceData();
  }, []);

  const filterByTime = (items, dateField) => {
    if (!items?.length) return [];
    if (timeFilter === 'All Time') return items;
    const now = new Date();
    const start = new Date();
    if (timeFilter === 'This Year') {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      start.setMonth(q * 3, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Month') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
    }
    return items.filter(it => {
      let dt = it[dateField];
      // If dateField is receivedDate and it's not available, fall back to createdAt
      if (dateField === 'receivedDate' && (!dt || !(dt instanceof Date))) {
        dt = it.createdAt || it.firstSeenAt || it.firstContactAt;
      }
      return dt instanceof Date && dt >= start;
    });
  };

  // Filter leads based on active tab
  const leads = useMemo(() => {
    if (activeTab === 'Lost') {
      return allLeads.filter(l => {
        const result = l.result || l.Result || l.leadResult || l.outcome;
        return result && result.toString().toLowerCase() === 'lost';
      });
    }
    if (activeTab === 'Won') {
      return allLeads.filter(l => {
        const result = l.result || l.Result || l.leadResult || l.outcome;
        return result && result.toString().toLowerCase() === 'won';
      });
    }
    return allLeads;
  }, [allLeads, activeTab]);

  const filteredLeads = useMemo(() => {
    if (!leads?.length) return [];
    // For Lost tab, prefer lostAt; for Won tab, prefer wonAt; for All tab, use receivedDate
    let dateField = 'receivedDate';
    if (activeTab === 'Lost' && leads.some(l => l.lostAt instanceof Date)) {
      dateField = 'lostAt';
    } else if (activeTab === 'Won' && leads.some(l => l.wonAt instanceof Date)) {
      dateField = 'wonAt';
    } else if (activeTab === 'Won' && leads.some(l => l.convertedAt instanceof Date)) {
      dateField = 'convertedAt';
    }
    const filtered = filterByTime(leads, dateField);
    
    // Sort by receivedDate (or createdAt) descending (latest first) for All tab
    if (activeTab === 'All') {
      return [...filtered].sort((a, b) => {
        const dateA = a.receivedDate || a.createdAt || a.firstSeenAt || a.firstContactAt;
        const dateB = b.receivedDate || b.createdAt || b.firstSeenAt || b.firstContactAt;
        
        if (!(dateA instanceof Date) && !(dateB instanceof Date)) return 0;
        if (!(dateA instanceof Date)) return 1;
        if (!(dateB instanceof Date)) return -1;
        
        return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
      });
    }
    
    return filtered;
  }, [leads, timeFilter, activeTab]);

  // Pagination for all tabs
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  // Reset page when switching tabs or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, timeFilter, filteredLeads.length]);

  // Copies every lead in the current tab/timeframe — all pages, not just the visible one
  const copyAllLeads = async () => {
    if (!filteredLeads.length) return;
    const rows = [
      ['Company Name', 'Contact Name', 'Email', 'Phone'],
      ...filteredLeads.map(l => [getCompanyName(l), getContactName(l), getEmail(l), getPhone(l)]),
    ];
    const text = rows.map(r => r.map(v => (v || '').toString().replace(/\s+/g, ' ').trim()).join('\t')).join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy leads', e);
    }
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditReason(lead.substatus || lead.reason || lead.lostReason || '');
    setEditSource(lead.source || lead.channel || lead.campaign || '');
    setEditCompanyName(lead.company || lead.accountName || lead.businessName || lead.organization || '');
    setEditContactName(lead.contactName || lead.primaryContactName || lead.name || '');
    setEditEmail(lead.email || '');
    setEditPhone(lead.phone || '');
    setEditNotes(lead.lostNotes || lead.notes || '');
    setEditPostMortem(Boolean(lead.postMortem));
    const prior = lead.lastVisitedStage
      || lead.previousStage
      || (Array.isArray(lead.stageHistory) && lead.stageHistory.length > 1 ? lead.stageHistory[lead.stageHistory.length - 2]?.stage : null)
      || lead.stageBeforeLost
      || lead.pipelineStage
      || '';
    setEditStageBeforeLost(prior);
    setEditError('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editLead) return;
    setEditSaving(true);
    setEditError('');
    try {
      const ref = doc(db, 'accounts', editLead.id);
      await updateDoc(ref, {
        substatus: editReason || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
      });

      // Update local state
      setAllLeads(prev => prev.map(l => l.id === editLead.id ? {
        ...l,
        substatus: editReason || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
      } : l));

      setEditOpen(false);
      setEditLead(null);
    } catch (e) {
      console.error(e);
      setEditError('Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const totals = useMemo(() => {
    if (activeTab === 'Lost') {
      const qualified = filteredLeads.filter(l => {
        const isQualified = l.qualified === true || l.qualified === 'true' || l.isQualified === true || l.isQualified === 'true';
        return isQualified;
      }).length;
      const flagsCount = filteredLeads.reduce((total, l) => {
        const flags = l.flags || l.contactFlags || l.flagCount || (Array.isArray(l.flagArray) ? l.flagArray.length : 0);
        return total + (typeof flags === 'number' ? flags : (Array.isArray(flags) ? flags.length : 0));
      }, 0);
      return {
        total: filteredLeads.length,
        lost: filteredLeads.length,
        qualified,
        flagsCount,
      };
    }
    if (activeTab === 'Won') {
      const qualified = filteredLeads.filter(l => {
        const isQualified = l.qualified === true || l.qualified === 'true' || l.isQualified === true || l.isQualified === 'true';
        return isQualified;
      }).length;
      const flagsCount = filteredLeads.reduce((total, l) => {
        const flags = l.flags || l.contactFlags || l.flagCount || (Array.isArray(l.flagArray) ? l.flagArray.length : 0);
        return total + (typeof flags === 'number' ? flags : (Array.isArray(flags) ? flags.length : 0));
      }, 0);
      return {
        total: filteredLeads.length,
        won: filteredLeads.length,
        qualified,
        flagsCount,
      };
    }
    // For All tab
    const lost = filteredLeads.filter(l => {
      const result = l.result || l.Result || l.leadResult || l.outcome;
      return result && result.toString().toLowerCase() === 'lost';
    }).length;
    const won = filteredLeads.filter(l => {
      const result = l.result || l.Result || l.leadResult || l.outcome;
      return result && result.toString().toLowerCase() === 'won';
    }).length;
    const active = filteredLeads.filter(l => {
      const result = l.result || l.Result || l.leadResult || l.outcome;
      return !result || (result.toString().toLowerCase() !== 'lost' && result.toString().toLowerCase() !== 'won');
    }).length;
    const qualified = filteredLeads.filter(l => {
      const isQualified = l.qualified === true || l.qualified === 'true' || l.isQualified === true || l.isQualified === 'true';
      return isQualified;
    }).length;
    const qualifiedPercentage = filteredLeads.length > 0 
      ? Math.round((qualified / filteredLeads.length) * 100 * 10) / 10 
      : 0;
    return {
      total: filteredLeads.length,
      lost,
      won,
      active,
      qualified,
      qualifiedPercentage,
    };
  }, [filteredLeads, activeTab]);

  const lostByReason = useMemo(() => {
    const validReasons = ['Communication', 'Price', 'Value', 'Process'];
    const counts = {};
    filteredLeads.forEach(l => {
      const reason = l.resultReason || l.substatus || l.reason || l.lostReason || 'Unknown';
      // Only count valid reasons, group others as 'Unknown'
      const normalizedReason = validReasons.includes(reason) ? reason : 'Unknown';
      counts[normalizedReason] = (counts[normalizedReason] || 0) + 1;
    });
    // Ensure all valid reasons are included even if count is 0
    validReasons.forEach(reason => {
      if (!counts[reason]) counts[reason] = 0;
    });
    return Object.entries(counts)
      .filter(([reason]) => reason !== 'Unknown' || counts[reason] > 0)
      .map(([reason, count], idx) => ({ reason, count, color: colors[idx % colors.length] }));
  }, [filteredLeads]);

  const wonByReason = useMemo(() => {
    const validReasons = ['Growth', 'Systems', 'Departmental', 'Value'];
    const counts = {};
    filteredLeads.forEach(l => {
      const reason = l.resultReason || l.substatus || l.reason || l.wonReason || l.conversionReason || 'Unknown';
      // Only count valid reasons, group others as 'Unknown'
      const normalizedReason = validReasons.includes(reason) ? reason : 'Unknown';
      counts[normalizedReason] = (counts[normalizedReason] || 0) + 1;
    });
    // Ensure all valid reasons are included even if count is 0
    validReasons.forEach(reason => {
      if (!counts[reason]) counts[reason] = 0;
    });
    return Object.entries(counts)
      .filter(([reason]) => reason !== 'Unknown' || counts[reason] > 0)
      .map(([reason, count], idx) => ({ reason, count, color: colors[idx % colors.length] }));
  }, [filteredLeads]);

  const allByStage = useMemo(() => {
    const STAGES = ['Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal', 'Lost', 'Client'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    counts['Unknown'] = 0;
    filteredLeads.forEach(l => {
      const stage = l.stage || 'Unknown';
      if (STAGES.includes(stage)) {
        counts[stage] = (counts[stage] || 0) + 1;
      } else {
        counts['Unknown'] = (counts['Unknown'] || 0) + 1;
      }
    });
    const allStages = [...STAGES, 'Unknown'];
    return allStages.map((stage, idx) => ({ stage, count: counts[stage] || 0, color: colors[idx % colors.length] })).filter(s => s.count > 0);
  }, [filteredLeads]);

  const lostByStage = useMemo(() => {
    const STAGES = ['Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    counts['Unknown'] = 0;
    filteredLeads.forEach(l => {
      const stage = l.stage || 'Unknown';
      if (STAGES.includes(stage)) {
        counts[stage] = (counts[stage] || 0) + 1;
      } else {
        counts['Unknown'] = (counts['Unknown'] || 0) + 1;
      }
    });
    const allStages = [...STAGES, 'Unknown'];
    return allStages
      .filter(stage => counts[stage] > 0)
      .map((stage, idx) => ({ stage, count: counts[stage] || 0, color: colors[idx % colors.length] }));
  }, [filteredLeads]);

  const wonByStage = useMemo(() => {
    const STAGES = ['Lead Received', 'Discovery Call', 'Assessment', 'Onboarding Call', 'Proposal'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    counts['Unknown'] = 0;
    filteredLeads.forEach(l => {
      const stage = l.wonStage || l.stage || 'Unknown';
      if (STAGES.includes(stage)) {
        counts[stage] = (counts[stage] || 0) + 1;
      } else {
        counts['Unknown'] = (counts['Unknown'] || 0) + 1;
      }
    });
    const allStages = [...STAGES, 'Unknown'];
    return allStages
      .filter(stage => counts[stage] > 0)
      .map((stage, idx) => ({ stage, count: counts[stage] || 0, color: colors[idx % colors.length] }));
  }, [filteredLeads]);

  const sourceBreakdown = useMemo(() => {
    const counts = {};
    filteredLeads.forEach(l => {
      const raw = l.leadSource || l.source || l.channel || l.campaign || 'Unknown';
      const source = raw.toString().trim() || 'Unknown';
      counts[source] = (counts[source] || 0) + 1;
    });
    const total = filteredLeads.length || 1;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([source, count], idx) => ({
        source,
        count,
        share: Math.round((count / total) * 1000) / 10,
        color: sourceColors[idx % sourceColors.length],
      }));
  }, [filteredLeads]);

  const conferenceReport = useMemo(() => buildConferenceReport({
    conferences,
    accounts: attribution.accounts,
    purchases: attribution.purchases,
    periodStart: getPeriodStart(timeFilter),
  }), [conferences, attribution, timeFilter]);

  const qualifiedVsNonQualified = useMemo(() => {
    let qualified = 0;
    let nonQualified = 0;
    filteredLeads.forEach(l => {
      const isQualified = l.qualified === true || l.qualified === 'true' || l.isQualified === true || l.isQualified === 'true';
      if (isQualified) {
        qualified += 1;
      } else {
        nonQualified += 1;
      }
    });
    return [
      { name: 'Qualified', count: qualified, color: GLASS_TONE.mint },
      { name: 'Non-Qualified', count: nonQualified, color: GLASS_TONE.salmon }
    ];
  }, [filteredLeads]);

  const leadsOverTime = useMemo(() => {
    const now = new Date();
    const leadsByPeriod = {};
    
    // Use all leads for "All Time", filteredLeads for other time frames
    const leadsToProcess = timeFilter === 'All Time' ? leads : filteredLeads;
    
    if (!leadsToProcess.length) return [];
    
    // Determine which date field to use based on active tab
    // For leads count over time, we use Received Date (receivedDate) as the primary date field
    const getDateField = (lead) => {
      // Helper function to convert various date formats to Date object
      const parseDate = (dateValue) => {
        if (!dateValue) return null;
        if (dateValue instanceof Date) return dateValue;
        if (dateValue.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
        if (dateValue.seconds) return new Date(dateValue.seconds * 1000);
        if (typeof dateValue === 'string') return new Date(dateValue);
        return null;
      };

      // Won tab charts when the lead was won, matching the timeframe filter above
      if (activeTab === 'Won') {
        const wonDate = parseDate(lead.wonAt) || parseDate(lead.convertedAt);
        if (wonDate instanceof Date && !isNaN(wonDate.getTime())) return wonDate;
      }

      // Otherwise use receivedDate (Received Date from lead overview)
      const receivedDate = parseDate(lead.receivedDate);
      if (receivedDate instanceof Date && !isNaN(receivedDate.getTime())) {
        return receivedDate;
      }

      // Fallback to createdAt if receivedDate is not available
      const createdAt = parseDate(lead.createdAt) || parseDate(lead.firstSeenAt) || parseDate(lead.firstContactAt);
      if (createdAt instanceof Date && !isNaN(createdAt.getTime())) {
        return createdAt;
      }

      return null;
    };
    
    // Determine grouping strategy based on timeFilter
    if (timeFilter === 'All Time') {
      // Group by year
      leadsToProcess.forEach(lead => {
        const date = getDateField(lead);
        if (date instanceof Date) {
          const year = date.getFullYear().toString();
          leadsByPeriod[year] = (leadsByPeriod[year] || 0) + 1;
        }
      });
      
      // Get all years and sort them
      const years = Object.keys(leadsByPeriod).sort();
      return years.map(year => ({
        period: year,
        count: leadsByPeriod[year]
      }));
    } else if (timeFilter === 'This Year') {
      // Group by month (12 months)
      const months = [];
      for (let i = 0; i < 12; i++) {
        const monthDate = new Date(now.getFullYear(), i, 1);
        const monthKey = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        months.push({ period: monthKey, count: 0, date: monthDate });
      }
      
      leadsToProcess.forEach(lead => {
        const date = getDateField(lead);
        if (date instanceof Date && date.getFullYear() === now.getFullYear()) {
          const monthIndex = date.getMonth();
          months[monthIndex].count += 1;
        }
      });
      
      return months;
    } else if (timeFilter === 'This Quarter') {
      // Group by month (3 months)
      const q = Math.floor(now.getMonth() / 3);
      const months = [];
      for (let i = 0; i < 3; i++) {
        const monthDate = new Date(now.getFullYear(), q * 3 + i, 1);
        const monthKey = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        months.push({ period: monthKey, count: 0, date: monthDate });
      }
      
      leadsToProcess.forEach(lead => {
        const date = getDateField(lead);
        if (date instanceof Date) {
          const leadYear = date.getFullYear();
          const leadMonth = date.getMonth();
          if (leadYear === now.getFullYear() && leadMonth >= q * 3 && leadMonth < (q + 1) * 3) {
            const monthIndex = leadMonth - (q * 3);
            months[monthIndex].count += 1;
          }
        }
      });
      
      return months;
    } else if (timeFilter === 'This Month') {
      // Group by day
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const days = [];
      
      for (let i = 1; i <= daysInMonth; i++) {
        const dayDate = new Date(now.getFullYear(), now.getMonth(), i);
        const dayKey = dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        days.push({ period: dayKey, count: 0, date: dayDate });
      }
      
      leadsToProcess.forEach(lead => {
        const date = getDateField(lead);
        if (date instanceof Date) {
          const leadYear = date.getFullYear();
          const leadMonth = date.getMonth();
          const leadDay = date.getDate();
          if (leadYear === now.getFullYear() && leadMonth === now.getMonth()) {
            days[leadDay - 1].count += 1;
          }
        }
      });
      
      return days;
    }
    
    return [];
  }, [filteredLeads, leads, timeFilter, activeTab]);

  const avgDaysBeforeLost = useMemo(() => {
    if (!filteredLeads.length) return 0;
    const days = filteredLeads.map(l => {
      const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
      const end = l.lostAt || l.lastActivityAt || new Date();
      if (!(start instanceof Date) || !(end instanceof Date)) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    });
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredLeads]);

  const avgDaysBeforeWon = useMemo(() => {
    if (!filteredLeads.length) return 0;
    const days = filteredLeads.map(l => {
      const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
      const end = l.wonAt || l.convertedAt || l.lastActivityAt || new Date();
      if (!(start instanceof Date) || !(end instanceof Date)) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    });
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredLeads]);

  const avgDaysInPipeline = useMemo(() => {
    if (!filteredLeads.length) return 0;
    const days = filteredLeads.map(l => {
      const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
      const end = l.lastActivityAt || new Date();
      if (!(start instanceof Date) || !(end instanceof Date)) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    });
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredLeads]);

  const avgDaysInPipelineLost = useMemo(() => {
    if (!filteredLeads.length || activeTab !== 'Lost') return 0;
    const days = filteredLeads.map(l => {
      const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
      const end = l.lastActivityAt || new Date();
      if (!(start instanceof Date) || !(end instanceof Date)) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    });
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredLeads, activeTab]);

  const avgDaysInPipelineWon = useMemo(() => {
    if (!filteredLeads.length || activeTab !== 'Won') return 0;
    const days = filteredLeads.map(l => {
      const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
      const end = l.lastActivityAt || new Date();
      if (!(start instanceof Date) || !(end instanceof Date)) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    });
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredLeads, activeTab]);

  if (loading) {
    return (
      <div className="relative min-h-screen w-full">
        <GlassStyles />
        <GlassBackdrop />
        <div className="relative flex h-64 items-center justify-center text-lg text-gray-700 dark:text-white/70">Loading leads…</div>
      </div>
    );
  }

  const chart = glassChartTheme(darkMode);
  const pctOfTotal = (n) => (totals.total ? Math.round((n / totals.total) * 100) : 0);

  const stats = activeTab === 'Lost' ? [
    { icon: TrendingDown, value: totals.total, label: 'Total lost leads' },
    { icon: BadgeCheck, value: totals.qualified, label: 'Lost qualified leads', valueClassName: GLASS_TEXT.mint },
    { icon: Clock, value: avgDaysInPipelineLost, label: 'Avg days in pipeline' },
    { icon: Flag, value: totals.flagsCount, label: 'Flags' },
  ] : activeTab === 'Won' ? [
    { icon: Trophy, value: totals.total, label: 'Total won leads' },
    { icon: BadgeCheck, value: totals.qualified, label: 'Won qualified leads', valueClassName: GLASS_TEXT.mint },
    { icon: Clock, value: avgDaysInPipelineWon, label: 'Avg days in pipeline' },
    { icon: Flag, value: totals.flagsCount, label: 'Flags' },
  ] : [
    { icon: Users, value: totals.total, label: 'Total leads' },
    { icon: Activity, value: totals.active, label: 'Active leads', badge: `${pctOfTotal(totals.active)}%`, badgeTone: 'blue' },
    { icon: TrendingDown, value: totals.lost, label: 'Lost leads', badge: `${pctOfTotal(totals.lost)}%`, badgeTone: 'salmon' },
    { icon: BadgeCheck, value: `${totals.qualifiedPercentage}%`, label: 'Qualified lead %' },
    { icon: Clock, value: avgDaysInPipeline, label: 'Avg days in pipeline' },
  ];

  const overTimeColor = activeTab === 'Lost' ? GLASS_TONE.salmon : activeTab === 'Won' ? GLASS_TONE.mint : GLASS_TONE.lime;
  const overTimeTotal = leadsOverTime.reduce((sum, p) => sum + p.count, 0);
  const overTimeNoun = activeTab === 'Lost' ? 'lost leads' : activeTab === 'Won' ? 'won leads' : 'leads';
  // Month buckets read "Jan 2026"; the year is already implied by the timeframe
  const shortPeriod = (v) => (timeFilter === 'This Year' || timeFilter === 'This Quarter' ? String(v).split(' ')[0] : v);
  const barRadius = timeFilter === 'This Month' ? 5 : 12;

  const pageBtn = (active, disabled) => `h-9 min-w-[2.25rem] rounded-full px-3 text-sm font-medium tabular-nums transition ${
    active
      ? 'bg-[#D7F36B] text-gray-900'
      : disabled
        ? 'cursor-not-allowed border border-black/[0.05] text-gray-400 dark:border-white/[0.05] dark:text-white/20'
        : 'border border-black/10 text-gray-700 hover:bg-black/[0.04] dark:border-white/10 dark:text-white/70 dark:hover:bg-white/[0.06]'
  }`;

  const qualifiedBadge = (isQualified) => (
    <GlassBadge tone={isQualified ? 'lime' : 'neutral'}>{isQualified ? 'Yes' : 'No'}</GlassBadge>
  );
  const resultCell = (result) => (result && result !== '-'
    ? <GlassBadge tone={resultTone(result)}>{result}</GlassBadge>
    : <span className="text-gray-400 dark:text-white/30">-</span>);
  const companyCell = (companyName) => (
    <div className="flex items-center gap-3">
      <GlassAvatar name={companyName === '-' ? '' : companyName} />
      <span className="font-medium text-gray-900 dark:text-white">{companyName}</span>
    </div>
  );
  const muted = 'text-gray-500 dark:text-white/50';

  return (
    <div className="relative min-h-screen w-full">
      <GlassStyles />
      <GlassBackdrop />
      <div className="relative w-full p-4 md:p-6 lg:p-8">
        {/* Top bar: back + result switcher */}
        <div className="glass-rise grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
          <div>
            <GlassCircleButton onClick={() => navigate('/crm/reports')} title="Back to reports">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            </GlassCircleButton>
          </div>
          <div className="justify-self-center">
            <SlidingPills options={TAB_OPTIONS} value={activeTab} onChange={setActiveTab} ariaLabel="Lead result" />
          </div>
          <div />
        </div>

        {/* Title, headline numbers, timeframe + export */}
        <div className="mb-8 mt-8 flex flex-wrap items-center gap-x-10 gap-y-6">
          <h1 className="glass-rise text-4xl font-medium leading-[1.05] tracking-tight text-gray-900 dark:text-white">
            Leads<br />Reports
          </h1>
          {stats.map((s, i) => (
            <div key={`${activeTab}-${s.label}`} className="glass-rise" style={{ animationDelay: `${60 + i * 50}ms` }}>
              <GlassStat {...s} />
            </div>
          ))}
          <div className="glass-rise flex flex-wrap items-center gap-3 2xl:ml-auto" style={{ animationDelay: '120ms' }}>
            <SlidingPills size="sm" options={TIME_OPTIONS} value={timeFilter} onChange={setTimeFilter} ariaLabel="Timeframe" />
            <GlassAccentButton
              onClick={copyAllLeads}
              disabled={filteredLeads.length === 0}
              title="Copy company name, contact name, email and phone for every lead — all pages"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied!' : `Copy all (${filteredLeads.length})`}
            </GlassAccentButton>
          </div>
        </div>

        {/* Charts replay their rise whenever the tab or timeframe changes */}
        <div key={`${activeTab}-${timeFilter}`} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <GlassCard className="p-6 xl:col-span-2" delay={0}>
              <GlassCardHeader
                title={activeTab === 'Lost' ? 'Lost Leads Count Over Time' : activeTab === 'Won' ? 'Won Leads Count Over Time' : 'Leads Count Over Time'}
              />
              <GlassFigure className="mt-4" value={overTimeTotal.toLocaleString()} caption={`${overTimeNoun}, ${PERIOD_PHRASE[timeFilter] || ''}`} />
              <div className="mt-4 flex flex-wrap gap-2">
                {activeTab === 'All' ? (
                  <>
                    <GlassChip color={GLASS_TONE.mint} label="Won" value={totals.won} />
                    <GlassChip color={GLASS_TONE.salmon} label="Lost" value={totals.lost} />
                    <GlassChip color={GLASS_TONE.blue} label="Active" value={totals.active} />
                  </>
                ) : (
                  <>
                    <GlassChip color={GLASS_TONE.lime} label="Qualified" value={totals.qualified} />
                    <GlassChip color={GLASS_TONE.lavender} label="Flags" value={totals.flagsCount} />
                  </>
                )}
              </div>
              <div className="mt-5 h-72">
                {leadsOverTime.length === 0 ? (
                  <GlassEmpty className="h-full">
                    {activeTab === 'Lost' ? 'No lost leads data available' : activeTab === 'Won' ? 'No won leads data available' : 'No leads data available'}
                  </GlassEmpty>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={leadsOverTime} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barCategoryGap="16%">
                      <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                      <XAxis
                        dataKey="period"
                        tickFormatter={shortPeriod}
                        tick={{ fill: chart.tick, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={6}
                      />
                      <YAxis allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip cursor={{ fill: chart.cursor }} content={<GlassTooltip unit="leads" />} />
                      <Bar
                        dataKey="count"
                        name={activeTab === 'Lost' ? 'Lost Leads' : activeTab === 'Won' ? 'Won Leads' : 'Leads'}
                        fill={overTimeColor}
                        radius={[barRadius, barRadius, barRadius, barRadius]}
                        background={{ fill: chart.track, radius: barRadius }}
                        animationDuration={700}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </GlassCard>

            <SourcesCard data={sourceBreakdown} darkMode={darkMode} timeFilter={timeFilter} delay={80} />
          </div>

          {activeTab === 'Lost' && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <BreakdownCard title="Reasons Lost" data={lostByReason} labelKey="reason" emptyText="No lost leads recorded" darkMode={darkMode} delay={140} />
              <BreakdownCard title="Stage Where We Lost Them" data={lostByStage} labelKey="stage" emptyText="No stage data available" darkMode={darkMode} delay={200} />
            </div>
          )}

          {activeTab === 'Won' && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <BreakdownCard title="Reasons Won" data={wonByReason} labelKey="reason" emptyText="No won leads recorded" darkMode={darkMode} delay={140} />
              <BreakdownCard title="Stage Where We Won Them" data={wonByStage} labelKey="stage" emptyText="No stage data available" darkMode={darkMode} delay={200} />
            </div>
          )}

          {activeTab === 'All' && (
            <GlassCard className="p-6" delay={140}>
              <GlassCardHeader title="Qualified vs Non-Qualified" />
              {qualifiedVsNonQualified.every(q => q.count === 0) ? (
                <GlassEmpty className="h-64">No qualification data available</GlassEmpty>
              ) : (
                <div className="mt-4 grid grid-cols-1 items-center gap-6 lg:grid-cols-2">
                  <div className="h-64">
                    <GlassDonut data={qualifiedVsNonQualified} dataKey="count" nameKey="name" inner={88} outer={102} darkMode={darkMode} unit="leads">
                      <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{totals.qualifiedPercentage}%</div>
                      <div className="text-xs text-gray-500 dark:text-white/[0.45]">qualified</div>
                    </GlassDonut>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {qualifiedVsNonQualified.map((entry) => {
                      const pct = filteredLeads.length
                        ? Math.round((entry.count / filteredLeads.length) * 1000) / 10
                        : 0;
                      return (
                        <div key={entry.name} className={`${GLASS_INSET} flex flex-col items-center justify-center p-6 text-center`}>
                          <div className="mb-1 flex items-center gap-2 text-sm text-gray-600 dark:text-white/60">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                            {entry.name}
                          </div>
                          <div className="text-3xl font-semibold text-gray-900 tabular-nums dark:text-white">{entry.count}</div>
                          <div className="mt-1 text-sm text-gray-500 dark:text-white/40">{pct}% of leads</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </GlassCard>
          )}
        </div>

        <div className="mt-6 space-y-6">
          <ConferencesCard
            rows={conferenceReport.rows}
            totals={conferenceReport.totals}
            unspecified={conferenceReport.unspecified}
            timeFilter={timeFilter}
            onSelect={(id) => navigate(`/crm/conferences-reports/${id}`)}
            delay={240}
          />

          <GlassCard className="p-6" delay={300}>
            <GlassCardHeader
              title={activeTab === 'Lost' ? 'Lost Leads' : activeTab === 'Won' ? 'Won Leads' : 'All Leads'}
              to="/crm/leads"
              toTitle="Open leads"
              right={<span className={`text-xs ${muted}`}>{filteredLeads.length} leads</span>}
            />
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    {activeTab === 'All' ? (
                      <>
                        <th className={GLASS_TH}>Company Name</th>
                        <th className={GLASS_TH}>Contact Name</th>
                        <th className={GLASS_TH}>Email</th>
                        <th className={GLASS_TH}>Phone</th>
                        <th className={GLASS_TH}>Result</th>
                        <th className={GLASS_TH}>Lead Received</th>
                      </>
                    ) : (
                      <>
                        <th className={GLASS_TH}>Company Name</th>
                        <th className={GLASS_TH}>Contact Name</th>
                        <th className={GLASS_TH}>Source</th>
                        {activeTab === 'Lost' ? (
                          <>
                            <th className={GLASS_TH}>Reason</th>
                            <th className={GLASS_TH}>Stage Lost</th>
                          </>
                        ) : activeTab === 'Won' ? (
                          <th className={GLASS_TH}>Stage Won</th>
                        ) : (
                          <th className={GLASS_TH}>Stage</th>
                        )}
                        <th className={GLASS_TH}>Qualified</th>
                        <th className={GLASS_TH}>Result</th>
                        <th className={GLASS_TH}>Days in Pipeline</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-white/75">
                  {paginatedLeads.map((l) => {
                    const source = l.leadSource || l.source || l.channel || l.campaign || 'Unknown';
                    const stage = l.stage || 'Unknown';
                    const companyName = getCompanyName(l) || '-';
                    const start = l.createdAt || l.firstSeenAt || l.firstContactAt;
                    const end = l.lastActivityAt || new Date();
                    const days = (start instanceof Date && end instanceof Date)
                      ? Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)))
                      : '-';
                    // For All tab, get received date
                    const receivedDate = activeTab === 'All'
                      ? (l.receivedDate instanceof Date
                        ? l.receivedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : (l.createdAt instanceof Date
                          ? l.createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '-'))
                      : null;
                    const contactName = getContactName(l) || '-';
                    const email = getEmail(l) || '-';
                    const phone = getPhone(l) || '-';
                    const isQualified = l.qualified === true || l.qualified === 'true' || l.isQualified === true || l.isQualified === 'true';
                    const result = l.result || l.Result || l.leadResult || l.outcome || '-';
                    const rowCls = `${GLASS_ROW} cursor-pointer`;

                    if (activeTab === 'Lost') {
                      const reason = l.resultReason || l.substatus || l.reason || l.lostReason || 'Unknown';
                      const lostDays = (() => {
                        const lostStart = l.createdAt || l.firstSeenAt || l.firstContactAt;
                        const lostEnd = l.lostAt || l.lastActivityAt || new Date();
                        if (lostStart instanceof Date && lostEnd instanceof Date) {
                          return Math.max(0, Math.round((lostEnd - lostStart) / (1000 * 60 * 60 * 24)));
                        }
                        return '-';
                      })();
                      return (
                        <tr key={l.id} className={rowCls} onClick={() => navigate(l.isClient ? `/crm/client/${l.id}` : `/crm/lead/${l.id}`)}>
                          <td className="py-3 pr-4">{companyCell(companyName)}</td>
                          <td className="py-3 pr-4">{contactName}</td>
                          <td className="py-3 pr-4">{source}</td>
                          <td className="py-3 pr-4">{reason}</td>
                          <td className="py-3 pr-4">{stage}</td>
                          <td className="py-3 pr-4">{qualifiedBadge(isQualified)}</td>
                          <td className="py-3 pr-4">{resultCell(result)}</td>
                          <td className="py-3 tabular-nums">{lostDays}</td>
                        </tr>
                      );
                    }
                    if (activeTab === 'Won') {
                      const won = l.wonAt || l.convertedAt;
                      const wonDays = (() => {
                        const wonStart = l.createdAt || l.firstSeenAt || l.firstContactAt;
                        const wonEnd = won instanceof Date ? won : (won?.toDate ? won.toDate() : (l.lastActivityAt || new Date()));
                        if (wonStart instanceof Date && wonEnd instanceof Date) {
                          return Math.max(0, Math.round((wonEnd - wonStart) / (1000 * 60 * 60 * 24)));
                        }
                        return '-';
                      })();
                      return (
                        <tr key={l.id} className={rowCls} onClick={() => navigate(l.isClient ? `/crm/client/${l.id}` : `/crm/lead/${l.id}`)}>
                          <td className="py-3 pr-4">{companyCell(companyName)}</td>
                          <td className="py-3 pr-4">{contactName}</td>
                          <td className="py-3 pr-4">{source}</td>
                          <td className="py-3 pr-4">{l.wonStage || stage}</td>
                          <td className="py-3 pr-4">{qualifiedBadge(isQualified)}</td>
                          <td className="py-3 pr-4">{resultCell(result)}</td>
                          <td className="py-3 tabular-nums">{wonDays}</td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={l.id} className={rowCls} onClick={() => navigate(l.isClient ? `/crm/client/${l.id}` : `/crm/lead/${l.id}`)}>
                        {activeTab === 'All' ? (
                          <>
                            <td className="py-3 pr-4">{companyCell(companyName)}</td>
                            <td className="py-3 pr-4">{contactName}</td>
                            <td className={`py-3 pr-4 ${muted}`}>{email}</td>
                            <td className={`py-3 pr-4 whitespace-nowrap ${muted}`}>{phone}</td>
                            <td className="py-3 pr-4">{resultCell(result)}</td>
                            <td className="py-3 whitespace-nowrap">{receivedDate}</td>
                          </>
                        ) : (
                          <>
                            <td className="py-3 pr-4">{companyCell(companyName)}</td>
                            <td className="py-3 pr-4">{contactName}</td>
                            <td className="py-3 pr-4">{source}</td>
                            <td className="py-3 pr-4">
                              <GlassBadge tone={stage === 'Lost' ? 'salmon' : stage === 'Client' || stage === 'Won' ? 'mint' : 'blue'}>{stage}</GlassBadge>
                            </td>
                            <td className="py-3 pr-4">{qualifiedBadge(isQualified)}</td>
                            <td className="py-3 pr-4">{resultCell(result)}</td>
                            <td className="py-3 tabular-nums">{days}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                  {filteredLeads.length === 0 && (
                    <tr>
                      <td colSpan={activeTab === 'Lost' ? 8 : activeTab === 'Won' ? 7 : activeTab === 'All' ? 6 : 7} className={`py-8 text-center ${muted}`}>
                        No {activeTab === 'Lost' ? 'lost ' : activeTab === 'Won' ? 'won ' : ''}leads found for selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination controls for all tabs */}
            {filteredLeads.length > 10 && (
              <div className="mt-5 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={pageBtn(false, currentPage === 1)}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button key={page} type="button" onClick={() => setCurrentPage(page)} className={pageBtn(currentPage === page, false)}>
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className={`px-1 ${muted}`}>
                        …
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={pageBtn(false, currentPage === totalPages)}
                >
                  Next
                </button>
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Kept outside the glass cards: backdrop-filter would trap a fixed overlay inside them */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !editSaving && setEditOpen(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-[28px] border border-white/70 bg-white/[0.85] p-6 text-gray-900 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#181614]/90 dark:text-white">
            <div className="mb-4 text-center">
              <h3 className="text-lg font-medium">Edit Lost Lead</h3>
            </div>
            <div className="space-y-4">
              {/* Details overview */}
              <div className={`${GLASS_INSET} p-4`}>
                {editLead && (() => {
                  const created = editLead.createdAt instanceof Date ? editLead.createdAt.toLocaleDateString() : '-';
                  const lost = editLead.lostAt instanceof Date ? editLead.lostAt.toLocaleDateString() : '-';
                  const lastAct = editLead.lastActivityAt instanceof Date ? editLead.lastActivityAt.toLocaleDateString() : '-';
                  const start = editLead.createdAt || editLead.firstSeenAt || editLead.firstContactAt;
                  const end = editLead.lostAt || editLead.lastActivityAt || new Date();
                  const days = (start instanceof Date && end instanceof Date)
                    ? Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)))
                    : '-';
                  const createdBy = editLead.createdByName || editLead.createdBy || editLead.ownerName || editLead.owner || editLead.ownerId || '-';
                  const displaySource = editLead.source || editLead.channel || editLead.campaign || '-';
                  return (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className={muted}>Days in Pipeline</div>
                        <div className="font-medium">{days}</div>
                      </div>
                      <div>
                        <div className={muted}>Created</div>
                        <div className="font-medium">{created}</div>
                      </div>
                      <div>
                        <div className={muted}>Lost</div>
                        <div className="font-medium">{lost}</div>
                      </div>
                      <div>
                        <div className={muted}>Last Activity</div>
                        <div className="font-medium">{lastAct}</div>
                      </div>
                      <div className="col-span-2">
                        <div className={muted}>Created By</div>
                        <div className="font-medium">{createdBy}</div>
                      </div>
                      <div className="col-span-2">
                        <div className={muted}>Source</div>
                        <div className="font-medium">{displaySource}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Company Name</label>
                  <input type="text" value={editCompanyName} onChange={(e) => setEditCompanyName(e.target.value)} className={GLASS_FIELD} placeholder="Acme Co." disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Contact Name</label>
                  <input type="text" value={editContactName} onChange={(e) => setEditContactName(e.target.value)} className={GLASS_FIELD} placeholder="Jane Doe" disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Email</label>
                  <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className={GLASS_FIELD} placeholder="jane@example.com" disabled={editSaving} />
                </div>
                <div>
                  <label className={`mb-1 block text-sm ${muted}`}>Phone</label>
                  <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className={GLASS_FIELD} placeholder="(555) 555-5555" disabled={editSaving} />
                </div>
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Reason</label>
                <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)} className={GLASS_FIELD} placeholder="e.g., Budget constraints" disabled={editSaving} />
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Source</label>
                <input type="text" value={editSource} onChange={(e) => setEditSource(e.target.value)} className={GLASS_FIELD} placeholder="e.g., Website, Referral, Facebook" disabled={editSaving} />
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Stage Lost</label>
                <select value={editStageBeforeLost} onChange={(e) => setEditStageBeforeLost(e.target.value)} className={GLASS_FIELD} disabled={editSaving}>
                  <option value="">Select stage</option>
                  <option value="Lead Received">Lead Received</option>
                  <option value="Discovery Call">Discovery Call</option>
                  <option value="Assessment">Assessment</option>
                  <option value="Onboarding Call">Onboarding Call</option>
                  <option value="Proposal">Proposal</option>
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm ${muted}`}>Notes</label>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={GLASS_FIELD} placeholder="Add context" disabled={editSaving} />
              </div>
              {editError && (
                <div className="text-sm text-red-500">{editError}</div>
              )}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setEditPostMortem(prev => !prev)}
                  disabled={editSaving}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    editPostMortem
                      ? 'bg-[#7FD9B0] text-gray-900'
                      : 'border border-black/10 text-gray-700 dark:border-white/10 dark:text-white/70'
                  } ${editSaving ? 'opacity-70' : ''}`}
                >
                  Post Mortem
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-gray-700 dark:border-white/10 dark:text-white/70"
                    onClick={() => setEditOpen(false)}
                    disabled={editSaving}
                  >
                    Cancel
                  </button>
                  <GlassAccentButton onClick={saveEdit} disabled={editSaving}>
                    {editSaving ? 'Saving…' : 'Save'}
                  </GlassAccentButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LostLeadsReports;
